import Link from 'next/link';
import { cn } from 'ui/lib/utils';

export interface BrandLogo {
  name: string;
  /** Logo image URL (StaticImageData.src). */
  src: string;
  /** Intrinsic dimensions — used to reserve space so nothing pops in. */
  width: number;
  height: number;
  /** Brand page the logo links to. */
  href: string;
}

interface BrandsMarqueeProps {
  brands: BrandLogo[];
  /** Seconds for one full loop. */
  speed?: number;
  className?: string;
}

/**
 * Seamless scrolling row of brand logos — full colour by default, grayscale on
 * hover — edge-masked and paused while hovered. Items are duplicated so the
 * -50% translate loops seamlessly. Each logo links to its brand page. Sized in
 * px for the 10px rem root.
 */
const BrandsMarquee = ({ brands, speed = 40, className }: BrandsMarqueeProps) => {
  if (!brands.length) return null;
  const duplicated = [...brands, ...brands];

  return (
    <div
      data-slot="brands-marquee"
      aria-label="Brands we carry"
      className={cn(
        'group relative overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]',
        className
      )}
    >
      <div
        className="flex w-max items-center gap-[48px] animate-gf-marquee hover:[animation-play-state:paused]"
        style={{ animationDuration: `${speed}s` }}
      >
        {duplicated.map((brand, i) => {
          const clone = i >= brands.length;
          return (
            <Link
              key={`${brand.name}-${i}`}
              href={brand.href}
              aria-label={brand.name}
              aria-hidden={clone || undefined}
              tabIndex={clone ? -1 : undefined}
              className="shrink-0 transition duration-200 hover:grayscale"
            >
              {/* Plain img: the marquee duplicates every logo, and next/image's
                  required fixed dimensions fight a uniform-height row. width/
                  height reserve the aspect ratio so the logo occupies its space
                  before it loads; eager (no lazy) so the off-screen duplicate
                  set is ready before it scrolls in — no mid-scroll gap. */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={brand.src}
                alt={brand.name}
                width={brand.width}
                height={brand.height}
                className="h-[28px] w-auto object-contain"
              />
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default BrandsMarquee;
